import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import { AvatarWithName } from "../../../components/Avatar";
import { ClassLinkOrCleverIcon } from "../../../components/ClassLinkOrCleverIcon";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import SearchFilter from "../../../components/input/SearchFilter";
import SimplePagination from "../../../components/SimplePagination";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import { externalIdLabel } from "../../users/usersUtil";
import { CourseUserViewModel } from "../coursesApiSlice";
import AddUserToCourseButton from "./AddUserToCourseButton";
import RemoveUsersFromCourseButton from "./RemoveUsersFromCourseButton";

interface CourseUserTableModel extends CourseUserViewModel {
	isRowSelected: boolean;
}

interface CourseUsersTableProps {
	users: CourseUserViewModel[];
	courseId: number;
	schoolId: number;
	districtId: number;
	displayLength: number;
	isTeacherTable: boolean;
	seatsRemaining?: number;
	showSeatsWarning?: boolean;
	setErrorMessage?: (message: string) => void;
}

export default function CourseUsersTable(props: CourseUsersTableProps) {
	const { config } = useAppSelector((state) => state.site);
	const hasClassLinkOrCleverUser = props.users?.some((u) => u.classLinkId || u.cleverId);

	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [isFiltered, setIsFiltered] = useState(false);
	const [sortField, setSortField] = useState<string>();
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);

	const [selectedUsers, setSelectedUsers] = useState<CourseUserViewModel[]>([]);
	const [isRemovingUser, setIsRemovingUser] = useState(false);

	function resetTable() {
		setSelectedUsers([]);
		setIsRemovingUser(false);
		setPage(0);
	}

	const filteredUsers =
		props.users.filter((user) => {
			if (!searchFilter) return true;
			const searchLower = searchFilter.toLowerCase();
			return (
				user.name?.toLowerCase().includes(searchLower) ||
				user.username?.toLowerCase().includes(searchLower) ||
				user.externalId?.toLowerCase().includes(searchLower)
			);
		}) ?? [];

	const sortedUsers = useMemo(() => {
		if (!sortField || sortOrder === 0 || !sortOrder) {
			return filteredUsers;
		}

		const sorted = [...filteredUsers].sort((a, b) => {
			let aValue: any;
			let bValue: any;

			if (sortField === "lastLoggedIn") {
				aValue = a.lastLoggedIn ? new Date(a.lastLoggedIn).getTime() : null;
				bValue = b.lastLoggedIn ? new Date(b.lastLoggedIn).getTime() : null;
			} else {
				aValue = (a[sortField as keyof CourseUserViewModel] as string)?.toLowerCase() || "";
				bValue = (b[sortField as keyof CourseUserViewModel] as string)?.toLowerCase() || "";
			}

			// Handle null/undefined values - always place nulls at the end
			if (aValue == null && bValue == null) return 0;
			if (aValue == null) return 1; // a (null) goes after b
			if (bValue == null) return -1; // b (null) goes after a

			if (aValue < bValue) return -sortOrder;
			if (aValue > bValue) return sortOrder;
			return 0;
		});

		return sorted;
	}, [filteredUsers, sortField, sortOrder]);

	const onSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	const paginatedUsers = sortedUsers.slice(
		page * props.displayLength,
		(page + 1) * props.displayLength
	);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	useEffect(() => {
		setIsFiltered(!!searchFilter);
		setPage(0);
	}, [searchFilter]);

	const checkboxBodyTemplate = (row: CourseUserTableModel) => {
		return (
			<input
				type="checkbox"
				value={row.userId}
				checked={row.isRowSelected}
				onChange={handleSelectOne}
				className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
				aria-label={`Select user: ${row.name}`}
			/>
		);
	};

	const nameBodyTemplate = (row: CourseUserTableModel) => {
		return (
			<AvatarWithName
				userId={Number(row.userId)}
				name={row.name}
				initials={row.initials}
				hasImage={row.hasImage}
			/>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: CourseUserTableModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={!!row.classLinkId}
				isCleverEntity={!!row.cleverId}
			/>
		);
	};

	const lastLoggedInBodyTemplate = (row: CourseUserTableModel) => {
		return (
			<span>
				<FormattedDateAndTime date={row.lastLoggedIn} />
			</span>
		);
	};

	const handleSelectOne = (e: ChangeEvent<HTMLInputElement>) => {
		const userId = e.currentTarget.value;
		const isChecked = e.currentTarget.checked;
		const user = props.users.find((u) => String(u.userId) === userId);
		if (!user) return;
		if (isChecked) {
			setSelectedUsers((prev) => [...prev, user]);
		} else {
			setSelectedUsers((prev) => prev.filter((u) => u.userId !== user.userId));
		}
	};

	const handleSelectAll = (isChecked: boolean) => {
		if (isChecked) {
			// Add any missing users from filteredUsers to selectedUsers
			setSelectedUsers((prev) => {
				const newUsers = filteredUsers.filter(
					(fu) => !prev.some((su) => su.userId === fu.userId)
				);
				return [...prev, ...newUsers];
			});
		} else {
			// Remove all filteredUsers from selectedUsers
			setSelectedUsers((prev) =>
				prev.filter((su) => !filteredUsers.some((fu) => fu.userId === su.userId))
			);
		}
	};

	const allChecked = useMemo(() => {
		return (
			filteredUsers.length > 0 &&
			filteredUsers.every((u) => selectedUsers.some((su) => su.userId === u.userId))
		);
	}, [filteredUsers, selectedUsers]);

	const usersWithSelection: CourseUserTableModel[] = useMemo(() => {
		return paginatedUsers.map((user) => ({
			...user,
			isRowSelected: selectedUsers.some((u) => u.userId === user.userId),
		}));
	}, [paginatedUsers, selectedUsers]);

	return (
		<section>
			<div className="flex justify-between gap-3 mt-4 items-end">
				<SearchFilter
					id={`${props.isTeacherTable ? "teacher" : "student"}Search`}
					name={`${props.isTeacherTable ? "teacher" : "student"}Search`}
					searchFilter={searchFilter}
					setSearchFilter={setSearchFilter}
					label="Search"
					placeHolder={`Search by name, username or ${externalIdLabel(
						props.isTeacherTable ? UserRole.Proctor : UserRole.Writer,
						config,
						true
					)}...`}
					className="w-96"
				/>
				<div className="flex gap-x-3 items-end">
					<AddUserToCourseButton
						courseId={props.courseId}
						schoolId={props.schoolId}
						districtId={props.districtId}
						isAddingTeacher={props.isTeacherTable}
						seatsRemaining={props.seatsRemaining ?? 0}
						showSeatsWarning={props.showSeatsWarning ?? false}
						setErrorMessage={props.setErrorMessage}
					/>
					{props.users.length > 0 && (
						<RemoveUsersFromCourseButton
							isRemovingTeacher={props.isTeacherTable}
							selectedUsers={selectedUsers}
							courseId={props.courseId}
							isLoading={isRemovingUser}
							resetTable={resetTable}
							setIsLoading={setIsRemovingUser}
						/>
					)}
				</div>
			</div>
			<DataTable
				value={usersWithSelection}
				loading={isRemovingUser}
				dataKey="userId"
				emptyMessage={`No ${props.isTeacherTable ? "teachers" : "students"} found`}
				stripedRows
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ paddingTop: "16px" }}
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				sortIcon={CustomSortIcon}>
				<Column
					columnKey="user-checkbox"
					header={
						<input
							type="checkbox"
							checked={allChecked}
							onChange={(e) => handleSelectAll(e.target.checked)}
							className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
							aria-label="Select all users"
						/>
					}
					body={checkboxBodyTemplate}
					className="w-12"
					headerClassName="text-center"
					bodyClassName="text-center"
				/>
				<Column header="Name" body={nameBodyTemplate} sortable sortField="name"></Column>
				{!config?.isUtahComposeSite && hasClassLinkOrCleverUser && (
					<Column
						body={cleverOrClassLinkBodyTemplate}
						headerClassName="!px-0 w-4"
						className="!px-0"></Column>
				)}
				<Column header="Username" field="username" sortable></Column>
				<Column
					header={externalIdLabel(
						props.isTeacherTable ? UserRole.Proctor : UserRole.Writer,
						config
					)}
					field="externalId"
					sortable></Column>
				{props.isTeacherTable && <Column header="Email" field="email" sortable></Column>}
				<Column
					header="Last Login Date"
					body={lastLoggedInBodyTemplate}
					sortable
					sortField="lastLoggedIn"
				/>
			</DataTable>
			<SimplePagination
				itemCount={sortedUsers?.length}
				page={page}
				pageSize={props.displayLength}
				isFiltered={isFiltered}
				totalCount={props.users?.length}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
